import { useState, useEffect } from 'react';
import { useNavigate, Link, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { API, getAuthHeaders } from '../App';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import {
  ArrowLeft, Bell, Mail, MessageCircle, Smartphone, Save,
  RefreshCw, Send, CheckCircle, AlertTriangle
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Switch } from '../components/ui/switch';
import LanguageSwitcher from '../components/LanguageSwitcher';

function AdminNotifications({ user, onLogout }) {
  const { t, i18n } = useTranslation();
  const { lang } = useParams();
  const currentLang = lang || i18n.language || 'ru';
  const navigate = useNavigate();
  
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testingSms, setTestingSms] = useState(false);
  const [testingTelegram, setTestingTelegram] = useState(false);

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      const response = await axios.get(`${API}/admin/notification-settings`, {
        headers: getAuthHeaders()
      });
      setSettings(response.data);
    } catch (error) {
      toast.error('Не удалось загрузить настройки');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/admin/notification-settings`, settings, {
        headers: getAuthHeaders()
      });
      toast.success('Настройки сохранены');
    } catch (error) {
      toast.error('Не удалось сохранить настройки');
    } finally {
      setSaving(false);
    }
  };

  const handleTestNotification = async (channel) => {
    if (channel === 'telegram') setTestingTelegram(true);
    else setTestingSms(true);
    
    try {
      const response = await axios.post(
        `${API}/admin/notification-settings/test?channel=${channel}`,
        {},
        { headers: getAuthHeaders() }
      );
      
      if (response.data.success) {
        toast.success(response.data.message);
      } else {
        toast.error(response.data.message);
      }
    } catch (error) {
      toast.error('Ошибка отправки');
    } finally {
      if (channel === 'telegram') setTestingTelegram(false);
      else setTestingSms(false);
    }
  };

  const updateSetting = (channel, key, value) => {
    setSettings(prev => ({
      ...prev,
      [channel]: {
        ...prev[channel],
        [key]: value
      }
    }));
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="flex flex-col items-center gap-3">
          <RefreshCw className="w-8 h-8 animate-spin text-emerald-600" />
          <div className="text-lg text-gray-600">Загрузка настроек...</div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="sticky top-0 z-50 bg-white border-b shadow-sm">
        <div className="max-w-[1600px] mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Link to={`/${currentLang}/admin`}>
                <Button variant="ghost" size="sm" data-testid="back-btn">
                  <ArrowLeft className="w-4 h-4 mr-2" />
                  Назад
                </Button>
              </Link>
              <div>
                <h1 className="text-2xl font-bold text-gray-900">Настройки уведомлений</h1>
                <p className="text-sm text-gray-500">Управление каналами уведомлений</p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <LanguageSwitcher />
              <Button 
                onClick={handleSave} 
                disabled={saving}
                className="bg-emerald-700 hover:bg-emerald-800"
              >
                {saving ? <RefreshCw className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
                Сохранить
              </Button>
            </div>
          </div>
        </div>
      </header>

      <div className="max-w-[1200px] mx-auto px-4 py-6">
        {settings && (
          <div className="space-y-6">
            {/* Email Notifications */}
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                      <Mail className="w-5 h-5 text-blue-600" />
                    </div>
                    <div>
                      <CardTitle>Email уведомления</CardTitle>
                      <CardDescription>Отправка уведомлений на почту</CardDescription>
                    </div>
                  </div>
                  <Button 
                    variant="outline" 
                    size="sm"
                    onClick={() => handleTestNotification('email')}
                    disabled={testingSms}
                  >
                    <Send className="w-4 h-4 mr-2" />
                    Тест
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                    <div>
                      <p className="font-medium">Новый заказ</p>
                      <p className="text-sm text-gray-500">Уведомление о новом заказе</p>
                    </div>
                    <Switch
                      checked={settings.email?.new_order}
                      onCheckedChange={(v) => updateSetting('email', 'new_order', v)}
                    />
                  </div>
                  <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                    <div>
                      <p className="font-medium">Смена статуса заказа</p>
                      <p className="text-sm text-gray-500">При изменении статуса</p>
                    </div>
                    <Switch
                      checked={settings.email?.order_status_change}
                      onCheckedChange={(v) => updateSetting('email', 'order_status_change', v)}
                    />
                  </div>
                  <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                    <div>
                      <p className="font-medium">Получен платёж</p>
                      <p className="text-sm text-gray-500">При поступлении оплаты</p>
                    </div>
                    <Switch
                      checked={settings.email?.payment_received}
                      onCheckedChange={(v) => updateSetting('email', 'payment_received', v)}
                    />
                  </div>
                  <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                    <div>
                      <p className="font-medium">Низкий остаток</p>
                      <p className="text-sm text-gray-500">Алерт о низких запасах</p>
                    </div>
                    <Switch
                      checked={settings.email?.low_stock_alert}
                      onCheckedChange={(v) => updateSetting('email', 'low_stock_alert', v)}
                    />
                  </div>
                  <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                    <div>
                      <p className="font-medium">Ежедневный отчёт</p>
                      <p className="text-sm text-gray-500">Сводка за день</p>
                    </div>
                    <Switch
                      checked={settings.email?.daily_report}
                      onCheckedChange={(v) => updateSetting('email', 'daily_report', v)}
                    />
                  </div>
                  <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                    <div>
                      <p className="font-medium">Еженедельный отчёт</p>
                      <p className="text-sm text-gray-500">Сводка за неделю</p>
                    </div>
                    <Switch
                      checked={settings.email?.weekly_report}
                      onCheckedChange={(v) => updateSetting('email', 'weekly_report', v)}
                    />
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Telegram Notifications */}
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                      <MessageCircle className="w-5 h-5 text-blue-500" />
                    </div>
                    <div>
                      <CardTitle>Telegram уведомления</CardTitle>
                      <CardDescription>Мгновенные уведомления в Telegram</CardDescription>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <Badge className="bg-green-100 text-green-800">
                      <CheckCircle className="w-3 h-3 mr-1" />
                      Подключено
                    </Badge>
                    <Button 
                      variant="outline" 
                      size="sm"
                      onClick={() => handleTestNotification('telegram')}
                      disabled={testingTelegram}
                    >
                      {testingTelegram ? <RefreshCw className="w-4 h-4 mr-2 animate-spin" /> : <Send className="w-4 h-4 mr-2" />}
                      Тест
                    </Button>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                    <div>
                      <p className="font-medium">Новый заказ</p>
                      <p className="text-sm text-gray-500">Мгновенное уведомление</p>
                    </div>
                    <Switch
                      checked={settings.telegram?.new_order}
                      onCheckedChange={(v) => updateSetting('telegram', 'new_order', v)}
                    />
                  </div>
                  <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                    <div>
                      <p className="font-medium">Смена статуса</p>
                      <p className="text-sm text-gray-500">При изменении статуса</p>
                    </div>
                    <Switch
                      checked={settings.telegram?.order_status_change}
                      onCheckedChange={(v) => updateSetting('telegram', 'order_status_change', v)}
                    />
                  </div>
                  <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                    <div>
                      <p className="font-medium">Получен платёж</p>
                      <p className="text-sm text-gray-500">При поступлении оплаты</p>
                    </div>
                    <Switch
                      checked={settings.telegram?.payment_received}
                      onCheckedChange={(v) => updateSetting('telegram', 'payment_received', v)}
                    />
                  </div>
                  <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                    <div>
                      <p className="font-medium">Низкий остаток</p>
                      <p className="text-sm text-gray-500">Алерт о низких запасах</p>
                    </div>
                    <Switch
                      checked={settings.telegram?.low_stock_alert}
                      onCheckedChange={(v) => updateSetting('telegram', 'low_stock_alert', v)}
                    />
                  </div>
                  <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                    <div>
                      <p className="font-medium">Ежедневный отчёт</p>
                      <p className="text-sm text-gray-500">Сводка за день в 20:00</p>
                    </div>
                    <Switch
                      checked={settings.telegram?.daily_report}
                      onCheckedChange={(v) => updateSetting('telegram', 'daily_report', v)}
                    />
                  </div>
                  <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                    <div>
                      <p className="font-medium">Еженедельный отчёт</p>
                      <p className="text-sm text-gray-500">Сводка за неделю</p>
                    </div>
                    <Switch
                      checked={settings.telegram?.weekly_report}
                      onCheckedChange={(v) => updateSetting('telegram', 'weekly_report', v)}
                    />
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Push Notifications (In-App Bell) */}
            <Card>
              <CardHeader>
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center">
                    <Smartphone className="w-5 h-5 text-purple-600" />
                  </div>
                  <div>
                    <CardTitle className="flex items-center gap-2">
                      Push-уведомления (Колокольчик)
                      <Badge className="bg-green-100 text-green-700 text-xs">Работает</Badge>
                    </CardTitle>
                    <CardDescription>Уведомления в личном кабинете клиента</CardDescription>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                    <div>
                      <p className="font-medium">Новый заказ</p>
                      <p className="text-sm text-gray-500">Уведомление о создании заказа</p>
                    </div>
                    <Switch 
                      checked={settings.push?.new_order}
                      onCheckedChange={(v) => updateSetting('push', 'new_order', v)}
                    />
                  </div>
                  <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                    <div>
                      <p className="font-medium">Смена статуса</p>
                      <p className="text-sm text-gray-500">Уведомление при изменении статуса</p>
                    </div>
                    <Switch 
                      checked={settings.push?.order_status_change}
                      onCheckedChange={(v) => updateSetting('push', 'order_status_change', v)}
                    />
                  </div>
                </div>
                <div className="mt-4 p-3 bg-green-50 border border-green-200 rounded-lg flex items-start gap-2">
                  <Bell className="w-5 h-5 text-green-600 shrink-0" />
                  <p className="text-sm text-green-800">
                    Push-уведомления отображаются в колокольчике в личном кабинете клиента. Клиент видит уведомления о статусе заказа в реальном времени.
                  </p>
                </div>
              </CardContent>
            </Card>

            {/* Info */}
            <Card className="bg-blue-50 border-blue-200">
              <CardContent className="py-4">
                <div className="flex items-start gap-3">
                  <Bell className="w-5 h-5 text-blue-600 mt-0.5" />
                  <div>
                    <p className="text-blue-800 font-medium">Совет</p>
                    <p className="text-blue-600 text-sm">
                      Для оптимальной работы рекомендуем включить уведомления о новых заказах в Telegram — это обеспечит мгновенную реакцию на заказы клиентов.
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        )}
      </div>
    </div>
  );
}

export default AdminNotifications;
